import { useEffect, useState } from 'react'
import { Loader2, MapPin, ArrowRight } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Badge } from '../../lib/shadcn/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { cn } from '../../lib/shadcn/utils'
import { toast } from '../../lib/shadcn/sonner'
import { useGetCustomerMergePreview, useMergeCustomers } from '../../hooks/backend/customersPage'
import { reportStoreLocatorSync, type StoreLocatorSyncResult } from './storeLocatorSync'
import { HelpText } from '../../components/HelpText'

interface Counts {
  orders: number
  open_orders: number
  totes: number
  communications: number
  email_log: number
  order_archive: number
  leads: number
}

interface Side {
  customer_id: string
  name: string
  company_name: string | null
  email: string | null
  woocommerce_id: string | null
  shipping_city: string | null
  active: boolean
  store_locator: boolean
  store_locator_post_id: number | null
  reorder_reminders: boolean
  counts: Counts
}

interface Preview {
  kept: Side
  merged: Side
  fills: Array<{ field: string; label: string; value: string }>
  woo_conflict: boolean
}

interface MergeResult {
  kept_name: string
  merged_name: string
  moved: Record<string, number>
  warnings: string[]
  store_locator_sync: StoreLocatorSyncResult | null
}

interface Props {
  // Exactly two customer IDs, as selected on the Customers page.
  customerIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onMerged: () => void
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

function CustomerCard({ side, keep, onPick }: { side: Side; keep: boolean; onPick: () => void }) {
  const c = side.counts
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'flex-1 min-w-0 text-left rounded-md border p-3 flex flex-col gap-1.5 transition-colors',
        keep ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-accent/50'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-foreground truncate">{side.name}</span>
        <Badge variant={keep ? 'default' : 'outline'} className="shrink-0">
          {keep ? 'Keep' : 'Merge & remove'}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {side.company_name && side.company_name !== side.name ? <div>{side.company_name}</div> : null}
        <div>{side.email || 'No email'}{side.shipping_city ? ` · ${side.shipping_city}` : ''}</div>
        <div>Zoho {side.customer_id}{side.woocommerce_id ? ` · Woo #${side.woocommerce_id}` : ' · not linked to Woo'}</div>
      </div>
      <div className="text-sm text-foreground">
        {plural(c.orders, 'order')}{c.open_orders > 0 ? ` (${c.open_orders} open)` : ''} · {plural(c.totes, 'tote')} ·{' '}
        {plural(c.communications + c.email_log, 'message')}
      </div>
      <div className="flex flex-wrap gap-1">
        {!side.active ? <Badge variant="outline" className="text-xs">Inactive</Badge> : null}
        {side.store_locator ? (
          <Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 mr-0.5" />Store locator</Badge>
        ) : null}
      </div>
    </button>
  )
}

export default function CustomerMergeDialog({ customerIds, open, onOpenChange, onMerged }: Props) {
  const getPreview = useGetCustomerMergePreview()
  const mergeCustomers = useMergeCustomers()
  const [keepId, setKeepId] = useState<string | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [merging, setMerging] = useState(false)
  const [deactivateInZoho, setDeactivateInZoho] = useState(true)

  const [a, b] = customerIds
  const otherId = keepId === a ? b : a

  const loadPreview = async (keep: string, merge: string, pickDefault: boolean) => {
    setLoading(true)
    try {
      const data = (await getPreview.trigger({ keep_id: keep, merge_id: merge })) as Preview
      // First load: keep whichever has more orders.
      if (pickDefault && data.merged.counts.orders > data.kept.counts.orders) {
        const swapped = (await getPreview.trigger({ keep_id: merge, merge_id: keep })) as Preview
        setKeepId(merge)
        setPreview(swapped)
      } else {
        setKeepId(keep)
        setPreview(data)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load the customers')
      onOpenChange(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open || !a || !b) return
    setPreview(null)
    setKeepId(null)
    setDeactivateInZoho(true)
    void loadPreview(a, b, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, a, b])

  const pick = (id: string) => {
    if (id === keepId || loading || merging || !a || !b) return
    void loadPreview(id, id === a ? b : a, false)
  }

  const handleMerge = async () => {
    if (!preview || !keepId || !otherId) return
    setMerging(true)
    try {
      const result = (await mergeCustomers.trigger({
        keep_id: keepId,
        merge_id: otherId,
        deactivate_in_zoho: deactivateInZoho,
      })) as MergeResult
      const moved = result.moved
      toast.success(
        `Merged ${result.merged_name} into ${result.kept_name} (${plural(moved['orders'] ?? 0, 'order')}, ${plural(moved['totes'] ?? 0, 'tote')} moved)`
      )
      for (const warning of result.warnings) toast.warning(warning)
      reportStoreLocatorSync(result.store_locator_sync)
      onMerged()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Merge failed')
    } finally {
      setMerging(false)
    }
  }

  const kept = preview?.kept
  const merged = preview?.merged
  const mc = merged?.counts
  const movedLines = mc
    ? [
        mc.orders ? plural(mc.orders, 'order') : null,
        mc.totes ? plural(mc.totes, 'tote') : null,
        mc.communications + mc.email_log ? plural(mc.communications + mc.email_log, 'CRM message') : null,
        mc.order_archive ? plural(mc.order_archive, 'archived order') : null,
        mc.leads ? plural(mc.leads, 'converted lead') : null,
      ].filter(Boolean)
    : []

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !merging) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Merge Customers</DialogTitle>
          <DialogDescription><HelpText>
            Pick the customer to keep. Everything on the other one moves to it, then the other one is removed
            from the Hub. You can undo a merge from Merge history.
          </HelpText></DialogDescription>
        </DialogHeader>

        {!preview || !kept || !merged ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className={cn('flex flex-col gap-4', loading && 'opacity-60 pointer-events-none')}>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch">
              {[kept, merged]
                .slice()
                .sort((x, y) => (x.customer_id === a ? -1 : y.customer_id === a ? 1 : 0))
                .map(side => (
                  <CustomerCard
                    key={side.customer_id}
                    side={side}
                    keep={side.customer_id === keepId}
                    onPick={() => pick(side.customer_id)}
                  />
                ))}
            </div>

            <div className="rounded-md border p-3 text-sm flex flex-col gap-2">
              <div className="flex items-center gap-1.5 font-medium text-foreground">
                {merged.name} <ArrowRight className="w-4 h-4" /> {kept.name}
              </div>
              <ul className="list-disc pl-5 space-y-1 text-foreground">
                <li>
                  {movedLines.length > 0
                    ? `Moves ${movedLines.join(', ')} to ${kept.name}.`
                    : `${merged.name} has no orders, totes or messages to move.`}
                </li>
                {preview.fills.length > 0 ? (
                  <li>
                    Fills in what {kept.name} is missing:
                    <ul className="list-none pl-0 mt-1 space-y-0.5 text-muted-foreground">
                      {preview.fills.map(f => (
                        <li key={f.field} className="truncate">
                          <span className="text-foreground">{f.label}:</span> {f.value}
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li>{kept.name}'s details stay as they are.</li>
                )}
                {preview.woo_conflict ? (
                  <li>
                    Both are linked to a WooCommerce customer. {kept.name} keeps Woo #{kept.woocommerce_id}; Woo #
                    {merged.woocommerce_id} isn't changed on the B2B site.
                  </li>
                ) : null}
                {merged.store_locator_post_id ? (
                  <li>
                    {merged.name}'s store on poppinwtx.com goes to the trash
                    {kept.store_locator ? '.' : `, and ${kept.name} goes on the store locator instead.`}
                  </li>
                ) : null}
                {!kept.active && merged.active ? <li>{kept.name} becomes active.</li> : null}
                {!merged.reorder_reminders && kept.reorder_reminders ? (
                  <li>{kept.name} is left out of reorder reminders, like {merged.name} was.</li>
                ) : null}
              </ul>
            </div>

            <div className="flex items-start gap-3 rounded-md border p-3">
              <Checkbox
                id="merge-zoho"
                checked={deactivateInZoho}
                onCheckedChange={value => setDeactivateInZoho(!!value)}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="merge-zoho">Mark {merged.name} inactive in Zoho Books</Label>
                <p className="text-xs text-muted-foreground"><HelpText>
                  Keeps new orders from going to it. Its Zoho invoices and sales orders stay on that contact; to move
                  those too, merge the two contacts in Zoho Books.
                </HelpText></p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={merging}>
            Cancel
          </Button>
          <Button onClick={() => void handleMerge()} disabled={!preview || loading || merging}>
            {merging ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Merging…
              </>
            ) : kept ? (
              `Merge into ${kept.name}`
            ) : (
              'Merge'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
